import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Phone,
  Calendar,
  AlertCircle,
  Clock,
  UserX,
  FileBarChart,
  LogOut,
  Building2,
  MessageSquareWarning,
  CalendarCheck,
  Send,
  Link2,
  Target,
  Settings,
  TrendingUp,
  Upload,
  History
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";

// Grouped navigation for Admin - Separating Leads and SDR
const adminNavGroups = [
  {
    label: null,
    items: [
      { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" }
    ]
  },
  {
    label: "Leads",
    items: [
      { icon: UserPlus, label: "All Leads", path: "/leads" },
      { icon: Phone, label: "Leads Follow-up", path: "/leads-follow-ups" }
    ]
  },
  {
    label: "SDR",
    items: [
      { icon: Clock, label: "Due", path: "/due", badge: "due" },
      { icon: AlertCircle, label: "Overdue", path: "/overdue", badge: "overdue" },
      { icon: Calendar, label: "Delayed", path: "/delayed", badge: "delayed" },
      { icon: UserX, label: "No Show", path: "/no-show", badge: "no-show" },
      { icon: Target, label: "SDR Follow-up", path: "/sdr-follow-ups" },
      { icon: Send, label: "No Show Campaign", path: "/no-show-campaign" }
    ]
  },
  {
    label: "Common",
    items: [
      { icon: CalendarCheck, label: "Bookings", path: "/bookings" },
      { icon: Users, label: "Customers", path: "/customers" },
      { icon: MessageSquareWarning, label: "Complaints", path: "/complaints" },
      { icon: History, label: "Calls Done", path: "/calls-done" }
    ]
  },
  {
    label: "Admin",
    items: [
      { icon: Building2, label: "Branches", path: "/branches" },
      { icon: Settings, label: "Branch Settings", path: "/branch-settings" },
      { icon: Users, label: "CRE Management", path: "/cre-management" },
      { icon: TrendingUp, label: "CRE Performance", path: "/cre-performance" },
      { icon: Link2, label: "API Integrations", path: "/settings/api-integrations" },
      { icon: Upload, label: "Data Import", path: "/settings/data-import" },
      { icon: FileBarChart, label: "Reports", path: "/reports" }
    ]
  }
];

// Navigation for CRE users (no admin settings)
const creNavGroups = [
  {
    label: null,
    items: [
      { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" }
    ]
  },
  {
    label: "Leads",
    items: [
      { icon: UserPlus, label: "My Leads", path: "/leads" },
      { icon: Phone, label: "Leads Follow-up", path: "/leads-follow-ups" }
    ]
  },
  {
    label: "SDR",
    items: [
      { icon: Clock, label: "Due", path: "/due", badge: "due" },
      { icon: AlertCircle, label: "Overdue", path: "/overdue", badge: "overdue" },
      { icon: Calendar, label: "Delayed", path: "/delayed", badge: "delayed" },
      { icon: UserX, label: "No Show", path: "/no-show", badge: "no-show" },
      { icon: Target, label: "SDR Follow-up", path: "/sdr-follow-ups" }
    ]
  },
  {
    label: "Common",
    items: [
      { icon: CalendarCheck, label: "Bookings", path: "/bookings" },
      { icon: Users, label: "My Customers", path: "/customers" },
      { icon: MessageSquareWarning, label: "Complaints", path: "/complaints" },
      { icon: History, label: "Calls Done", path: "/calls-done" },
      { icon: Upload, label: "Data Import", path: "/data-import" }
    ]
  }
];

const getBadgeClass = (badge) => {
  switch(badge) {
    case "due": return "bg-yellow-100 text-yellow-800";
    case "overdue": return "bg-orange-100 text-orange-800";
    case "delayed": return "bg-blue-100 text-blue-800";
    case "no-show": return "bg-red-100 text-red-800";
    default: return "";
  }
};

export const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const navGroups = isAdmin() ? adminNavGroups : creNavGroups;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <aside className="sidebar" data-testid="sidebar">
      {/* Logo/Brand */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#ED1C24] flex items-center justify-center">
            <span className="text-white font-bold text-sm">S</span>
          </div>
          <div>
            <h1 className="font-heading font-bold text-base text-foreground">
              Servall CRE-CRM
            </h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {isAdmin() ? "Admin" : "CRE"} Panel
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {navGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-1">
            {group.label && (
              <div className="sidebar-section mt-4 first:mt-0">
                {group.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      cn("sidebar-nav-item", isActive && "active")
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge && (
                      <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", getBadgeClass(item.badge))}>
                        !
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-border bg-gray-50/50">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-[#ED1C24] text-white flex items-center justify-center font-semibold text-sm">
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[10px] text-muted-foreground capitalize">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground hover:text-foreground"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
